/* eslint-disable no-bitwise */
/* eslint-disable no-restricted-properties */

/* This library is released under the MIT license, see https://github.com/nenadmarkus/picojs */

export interface InputImage {
  pixels: Uint8Array;
  nrows: number;
  ncols: number;
  ldim: number;
}

export interface RunCascadeOptions {
  shiftfactor: number;
  minsize: number;
  maxsize: number;
  scalefactor: number;
}

interface RawDetection {
  row: number;
  col: number;
  scale: number;
  score: number;
}

export interface DetectionBox {
  centerY: number;
  centerX: number;
  scale: number;
  score: number;
}

export function unpackCascade(bytes: Int8Array) {
  //
  const dview = new DataView(new ArrayBuffer(4));
  /*
            we skip the first 8 bytes of the cascade file
            (cascade version number and some data used during the learning process)
        */
  let p = 8;
  /*
            read the depth (size) of each tree first: a 32-bit signed integer
        */
  dview.setUint8(0, bytes[p + 0]);
  dview.setUint8(1, bytes[p + 1]);
  dview.setUint8(2, bytes[p + 2]);
  dview.setUint8(3, bytes[p + 3]);
  const tdepth = dview.getInt32(0, true);
  p += 4;
  /*
            next, read the number of trees in the cascade: another 32-bit signed integer
        */
  dview.setUint8(0, bytes[p + 0]);
  dview.setUint8(1, bytes[p + 1]);
  dview.setUint8(2, bytes[p + 2]);
  dview.setUint8(3, bytes[p + 3]);
  const ntrees = dview.getInt32(0, true);
  p += 4;
  /*
            read the actual trees and cascade thresholds
        */
  const tcodesArr: number[] = [];
  const tpredsArr: number[] = [];
  const threshArr: number[] = [];
  for (let t = 0; t < ntrees; t += 1) {
    // read the binary tests placed in internal tree nodes
    tcodesArr.push(0, 0, 0, 0);
    const sliced = bytes.slice(p, p + 4 * Math.pow(2, tdepth) - 4);
    for (let i = 0; i < sliced.length; i += 1) {
      tcodesArr.push(sliced[i]);
    }
    p = p + 4 * Math.pow(2, tdepth) - 4;
    // read the prediction in the leaf nodes of the tree
    for (let i = 0; i < Math.pow(2, tdepth); i += 1) {
      dview.setUint8(0, bytes[p + 0]);
      dview.setUint8(1, bytes[p + 1]);
      dview.setUint8(2, bytes[p + 2]);
      dview.setUint8(3, bytes[p + 3]);
      tpredsArr.push(dview.getFloat32(0, true));
      p += 4;
    }
    // read the threshold
    dview.setUint8(0, bytes[p + 0]);
    dview.setUint8(1, bytes[p + 1]);
    dview.setUint8(2, bytes[p + 2]);
    dview.setUint8(3, bytes[p + 3]);
    threshArr.push(dview.getFloat32(0, true));
    p += 4;
  }

  const tcodes = new Int8Array(tcodesArr);
  const tpreds = new Float32Array(tpredsArr);
  const thresh = new Float32Array(threshArr);

  /*
            construct the classification function from the read data
        */
  function classifyRegion(
    row: number,
    col: number,
    scale: number,
    pixels: Uint8Array,
    ldim: number
  ) {
    const r = 256 * row;
    const c = 256 * col;
    let root = 0;
    let o = 0.0;
    const pow2tdepth = Math.pow(2, tdepth) >> 0; // '>>0' transforms this number to int

    for (let i = 0; i < ntrees; i += 1) {
      let idx = 1;
      for (let j = 0; j < tdepth; j += 1) {
        // we use '>> 8' here to perform an integer division: this seems important for performance
        idx =
          2 * idx +
          +(
            pixels[
              ((r + tcodes[root + 4 * idx + 0] * scale) >> 8) * ldim +
                ((c + tcodes[root + 4 * idx + 1] * scale) >> 8)
            ] <=
            pixels[
              ((r + tcodes[root + 4 * idx + 2] * scale) >> 8) * ldim +
                ((c + tcodes[root + 4 * idx + 3] * scale) >> 8)
            ]
          );
      }

      o += tpreds[pow2tdepth * i + idx - pow2tdepth];

      if (o <= thresh[i]) return -1;

      root += 4 * pow2tdepth;
    }
    return o - thresh[ntrees - 1];
  }
  /*
            we're done
        */
  return classifyRegion;
}

export type ClassifyRegionFunction = ReturnType<typeof unpackCascade>;

export function runCascade(
  image: InputImage,
  classifyRegion: ClassifyRegionFunction,
  options: RunCascadeOptions
) {
  const { pixels, nrows, ncols, ldim } = image;
  const { shiftfactor, minsize, maxsize, scalefactor } = options;

  let scale = minsize;
  const detections: RawDetection[] = [];

  while (scale <= maxsize) {
    const step = Math.max(shiftfactor * scale, 1) >> 0; // '>>0' transforms this number to int
    const offset = (scale / 2 + 1) >> 0;

    for (let row = offset; row <= nrows - offset; row += step) {
      for (let col = offset; col <= ncols - offset; col += step) {
        const score = classifyRegion(row, col, scale, pixels, ldim);
        if (score > 0.0) detections.push({ row, col, scale, score });
      }
    }

    scale *= scalefactor;
  }

  return detections;
}

export function clusterDetections(
  detections: RawDetection[],
  iouthreshold: number
) {
  /*
            sort detections by their score
        */
  const dets = detections.sort((a, b) => b.score - a.score);
  /*
            this helper function calculates the intersection over union for two detections
        */
  function calculateIOU(det1: RawDetection, det2: RawDetection) {
    // unpack the position and size of each detection
    const r1 = det1.row;
    const c1 = det1.col;
    const s1 = det1.scale;
    const r2 = det2.row;
    const c2 = det2.col;
    const s2 = det2.scale;
    // calculate detection overlap in each dimension
    const overr = Math.max(
      0,
      Math.min(r1 + s1 / 2, r2 + s2 / 2) - Math.max(r1 - s1 / 2, r2 - s2 / 2)
    );
    const overc = Math.max(
      0,
      Math.min(c1 + s1 / 2, c2 + s2 / 2) - Math.max(c1 - s1 / 2, c2 - s2 / 2)
    );
    // calculate and return IoU
    return (overr * overc) / (s1 * s1 + s2 * s2 - overr * overc);
  }
  /*
            do clustering through non-maximum suppression
        */
  const assignments = new Array(dets.length).fill(0);
  const clusters: DetectionBox[] = [];
  for (let i = 0; i < dets.length; i += 1) {
    // is this detection assigned to a cluster?
    if (assignments[i] === 0) {
      // it is not:
      // now we make a cluster out of it and see whether some other detections belong to it
      let r = 0.0;
      let c = 0.0;
      let s = 0.0;
      let q = 0.0;
      let n = 0;
      for (let j = i; j < dets.length; j += 1) {
        if (calculateIOU(dets[i], dets[j]) > iouthreshold) {
          assignments[j] = 1;
          r += dets[j].row;
          c += dets[j].col;
          s += dets[j].scale;
          q += dets[j].score;
          n += 1;
        }
      }
      // make a cluster representative
      clusters.push({ centerY: r / n, centerX: c / n, scale: s / n, score: q });
    }
  }

  return clusters;
}

export function instantiateDetectionMemory(size: number) {
  /*
            initialize a circular buffer of `size` elements
        */
  let n = 0;
  const memory: RawDetection[][] = [];
  for (let i = 0; i < size; i += 1) memory.push([]);
  /*
            build a function that:
            (1) inserts the current frame's detections into the buffer;
            (2) merges all detections from the last `size` frames and returns them
        */
  function updateMemory(detections: RawDetection[]) {
    memory[n] = detections;
    n = (n + 1) % memory.length;
    let dets: RawDetection[] = [];
    for (let i = 0; i < memory.length; i += 1) dets = dets.concat(memory[i]);
    //
    return dets;
  }
  /*
            we're done
        */
  return updateMemory;
}

export type UpdateMemoryFunction = ReturnType<
  typeof instantiateDetectionMemory
>;
